import type { Collider } from "../camera/walk.js";
import type { FloorTheme } from "../brand.js";
import type { Marker, RoomLayout } from "./layout.js";
import type { DecoName } from "./models.js";
import { layoutTheme, seatPlan } from "./themes.js";
import type { Ctx } from "./themes.js";

/**
 * Where the furniture of a floor stands (pure; the scene turns the lists into instanced meshes).
 * Everything is placed in the room's own frame: `lateral` runs across the hall, `depth` from the
 * back wall (the elevator and stairs end) toward the door, exactly like `agentMarkers`. Positions
 * are building-local meters on the floor plane; `yaw` turns a model whose front is +Z to face the
 * door.
 */

export interface Frame {
  place(lateral: number, depth: number): [number, number];
  /** Yaw that turns a +Z-facing model toward the door. */
  yaw: number;
  /** Width across the hall and length from back to front, inside the walls. */
  width: number;
  length: number;
  /** True when the door wall runs along X (north/south door). */
  alongX: boolean;
}

export function roomFrame(room: RoomLayout): Frame {
  const { bounds } = room;
  const [nx, nz] = room.door.normal;
  const alongX = nx === 0;
  const place = (lateral: number, depth: number): [number, number] => {
    if (alongX) return [lateral, -nz * (Math.abs(nz) * bounds.maxZ - depth)];
    return [-nx * (Math.abs(nx) * bounds.maxX - depth), lateral];
  };
  return {
    place,
    yaw: Math.atan2(nx, nz),
    width: alongX ? bounds.maxX * 2 : bounds.maxZ * 2,
    length: alongX ? bounds.maxZ * 2 : bounds.maxX * 2,
    alongX,
  };
}

export interface Spot {
  x: number;
  z: number;
  yaw: number;
  scale: number;
}
/** A Poly Haven model of a theme (`models.ts` `DecoName`); `y` lifts it onto a cabinet, a wall... */
export interface Deco extends Spot {
  name: DecoName;
  y: number;
}
export interface Rect {
  x: number;
  z: number;
  /** World-axis-aligned size (X, Z). */
  w: number;
  d: number;
}

/** What a floor is used for: the lobby on the ground floor, the leadership floor on top, open offices between. */
export type FloorKind = "lobby" | "open" | "leader";

/** Which kind a floor is: level 0 is the lobby, the top of a tall building the leadership floor. */
export function floorKind(level: number, levels: readonly number[]): FloorKind {
  if (level === 0) return "lobby";
  const top = Math.max(...levels);
  return level === top && levels.length > 2 ? "leader" : "open";
}

/** The hand-built pieces of the themed floors (`kit.ts` draws them, all merged into three meshes). */
export type PropKind =
  | "counter"
  | "deskScreen"
  | "turnstile"
  | "glassPanel"
  | "glassFrame"
  | "accentWall"
  | "planter"
  | "loungeBench"
  | "divider"
  | "serverRack"
  | "floorGlow"
  | "booth"
  | "kitchen"
  | "island"
  | "stool"
  | "recBooth"
  | "greenScreen"
  | "tripod"
  | "softbox"
  | "screenWall"
  | "equipCase"
  | "longTable"
  | "pouf"
  | "wallPanel"
  | "filing"
  | "cooler"
  | "credenza"
  | "floorLamp";

/** Brand tone of a prop or panel: `P` primary, `A` accent. */
export type Tone = "P" | "A";

export interface Prop {
  kind: PropKind;
  x: number;
  z: number;
  /** Turns the prop's local +Z (its front) toward where it faces. */
  yaw: number;
  /** Local width (along its X) and depth (along its Z) in meters. */
  w: number;
  d: number;
  /** Height where the kind is scalable (0 = the kind's own). */
  h: number;
  /** Height above the floor surface for wall-mounted kinds. */
  y: number;
  tone?: Tone;
}

/** A flat textured quad on a wall or counter (logo wall, directory, boards...). */
export interface Panel {
  kind:
    "logoWall" | "logoPlate" | "directory" | "whiteboard" | "moodBoard" | "campaign" | "acoustic";
  /** Centre x / z; the quad faces `yaw` (its local +Z). */
  x: number;
  z: number;
  yaw: number;
  y: number;
  w: number;
  h: number;
  tone?: Tone;
}

export interface FurnitureLayout {
  kind: FloorKind;
  theme: FloorTheme;
  props: Prop[];
  panels: Panel[];
  desks: Spot[];
  chairs: Spot[];
  /** Desk monitors (and the meeting room display, which is bigger). */
  monitors: Spot[];
  keyboards: Spot[];
  mice: Spot[];
  /** Tall plants and low plants. */
  plants: { x: number; z: number; scale: number }[];
  /** Ceiling light panels. */
  lights: { x: number; z: number }[];
  rugs: (Rect & { color: string })[];
  /** Glass partitions of the leader's office. */
  glass: Rect[];
  table: Spot | null;
  /** Chairs of the meeting room, which are not office chairs. */
  meetingChairs: Spot[];
  /** The meeting room's wall display: centre, size and the facing. */
  display: { x: number; z: number; yaw: number } | null;
  sofas: Spot[];
  armchairs: Spot[];
  coffeeTables: Spot[];
  bookshelves: Spot[];
  shelves: Spot[];
  boards: Spot[];
  /** Break area: coffee cart, fridge. */
  coffeeCarts: Spot[];
  fridges: Spot[];
  /** Ceiling lamps hanging over the lounge and the leader's office. */
  pendants: { x: number; z: number }[];
  /** The reception desk of the lobby, with the company sign behind it. */
  reception: { x: number; z: number; yaw: number; w: number; d: number } | null;
  /** The Poly Haven pieces of the theme: lounge, decor, art, plants. */
  deco: Deco[];
  /** Everything else that blocks the walker (axis aligned, world X / Z sizes). */
  solids: Rect[];
}

const MAX_DESKS = 12;

/** The theme a floor of this kind gets when the scene does not name one. */
export const defaultTheme = (kind: FloorKind): FloorTheme =>
  kind === "lobby" ? "lobby" : kind === "leader" ? "leadership" : "generic";

/** The theme that is actually laid out: a hall too small for a theme's seating falls back to generic. */
export function effectiveTheme(
  room: Pick<RoomLayout, "bounds" | "door">,
  kind: FloorKind,
  theme?: FloorTheme | null,
): FloorTheme {
  const t = theme ?? defaultTheme(kind);
  if (t === "generic" || t === "lobby") return t;
  const dx = room.bounds.maxX - room.bounds.minX;
  const dz = room.bounds.maxZ - room.bounds.minZ;
  const alongX = room.door.normal[0] === 0;
  const f = { width: alongX ? dx : dz, length: alongX ? dz : dx };
  return seatPlan(t, f) ? t : "generic";
}

interface FrameRect {
  a0: number;
  a1: number;
  b0: number;
  b1: number;
}

export function furnitureLayout(
  room: RoomLayout,
  markers: readonly Marker[],
  options: { ground: boolean; kind?: FloorKind; theme?: FloorTheme | null },
): FurnitureLayout {
  const f = roomFrame(room);
  const kind = options.kind ?? "leader";
  const theme = effectiveTheme(room, kind, options.theme);
  const out: FurnitureLayout = {
    kind,
    theme,
    props: [],
    panels: [],
    desks: [],
    chairs: [],
    monitors: [],
    keyboards: [],
    mice: [],
    plants: [],
    lights: [],
    rugs: [],
    glass: [],
    table: null,
    meetingChairs: [],
    display: null,
    sofas: [],
    armchairs: [],
    coffeeTables: [],
    bookshelves: [],
    shelves: [],
    boards: [],
    coffeeCarts: [],
    fridges: [],
    pendants: [],
    reception: null,
    deco: [],
    solids: [],
  };
  const spot = (lateral: number, depth: number, scale = 1): Spot => {
    const [x, z] = f.place(lateral, depth);
    return { x, z, yaw: f.yaw, scale };
  };
  /** A spot that faces another point of the frame. */
  const facing = (
    lateral: number,
    depth: number,
    toLat: number,
    toDepth: number,
    scale = 1,
  ): Spot => {
    const [x, z] = f.place(lateral, depth);
    const [tx, tz] = f.place(toLat, toDepth);
    return { x, z, yaw: Math.atan2(tx - x, tz - z), scale };
  };
  /** A solid block: `w` across the hall, `d` from back to front, centred on a frame point. */
  const solid = (lateral: number, depth: number, w: number, d: number): Rect => {
    const [x, z] = f.place(lateral, depth);
    return { x, z, w: f.alongX ? w : d, d: f.alongX ? d : w };
  };
  // A workstation: the chair where the person sits, the desk, monitor, keyboard and mouse in front.
  const station = (lateral: number, depth: number, scale = 1, dual = false) => {
    out.chairs.push(spot(lateral, depth, scale));
    out.desks.push(spot(lateral, depth + 0.6 * scale, scale));
    if (dual) {
      for (const s of [-0.34, 0.34]) {
        out.monitors.push(spot(lateral + s * scale, depth + 0.85 * scale, scale * 0.9));
      }
    } else {
      out.monitors.push(spot(lateral, depth + 0.85 * scale, scale));
    }
    out.keyboards.push(spot(lateral, depth + 0.5 * scale, scale));
    out.mice.push(spot(lateral + 0.4 * scale, depth + 0.5 * scale, scale));
  };

  const span = f.width / 2;
  const plant = (lateral: number, depth: number, scale = 1) => {
    const [px, pz] = f.place(lateral, depth);
    out.plants.push({ x: px, z: pz, scale });
  };

  if (theme !== "generic") {
    themed(out, theme, f, { spot, solid, station, plant, facing });
  } else {
    const perRow = Math.max(1, Math.min(4, Math.floor((f.width - 4) / 3)));
    const workers = markers.filter((m) => m.role === "worker").length;
    station(0, 3.2, 1.1);
    // Workers' desks in rows, then empty desks in the free slots.
    const rows = Math.max(1, Math.min(3, Math.floor((f.length - 12) / 3) + 1));
    let placed = 0;
    for (let row = 0; row < rows && out.desks.length < MAX_DESKS; row++) {
      for (let col = 0; col < perRow; col++) {
        const lateral = (col - (perRow - 1) / 2) * 3;
        const depth = 7 + row * 3;
        if (depth > f.length - 4) continue;
        // Every worker gets a desk; empty floors still show a few.
        if (placed < Math.max(workers, 6)) station(lateral, depth);
        placed += 1;
      }
    }

    const big = f.width >= 16 && f.length >= 14;
    // The meeting room: a table with six chairs on a rug, a display on the side wall.
    if (big) {
      const lateral = span - 3.6;
      const depth = f.length - 6.5;
      // The model's long axis runs across the hall; turn it to run from back to front.
      out.table = { ...spot(lateral, depth), yaw: f.yaw + Math.PI / 2 };
      for (const s of [-1, 1]) {
        for (const d of [-0.95, 0, 0.95]) {
          out.meetingChairs.push(facing(lateral + s * 1.1, depth + d, lateral, depth + d));
        }
      }
      const [rx, rz] = f.place(lateral, depth);
      out.rugs.push({
        x: rx,
        z: rz,
        w: f.alongX ? 4.0 : 5.0,
        d: f.alongX ? 5.0 : 4.0,
        color: "#4a5f58",
      });
      const [dx, dz] = f.place(span - 0.45, depth);
      out.display = { x: dx, z: dz, yaw: f.alongX ? -Math.PI / 2 : Math.PI };
      out.boards.push(facing(lateral - 2.2, depth + 2.2, lateral, depth));
      out.solids.push(solid(lateral - 2.2, depth + 2.2, 0.9, 0.5));
    }

    // The break area, on the side opposite the meeting room.
    if (big) {
      const lateral = -(span - 2.6);
      const depth = f.length - 1.4;
      out.coffeeCarts.push({ ...spot(lateral, depth - 0.2), yaw: f.yaw + Math.PI });
      out.solids.push(solid(lateral, depth - 0.2, 2.1, 1.0));
      out.fridges.push({ ...spot(lateral + 2.0, depth + 0.2), yaw: f.yaw + Math.PI });
      out.solids.push(solid(lateral + 2.0, depth + 0.2, 0.8, 0.8));
      out.sofas.push(facing(lateral - 0.2, depth - 4.6, lateral - 0.2, depth - 2.0));
      out.solids.push(solid(lateral - 0.2, depth - 4.6, 1.9, 0.9));
      out.coffeeTables.push(spot(lateral - 0.2, depth - 3.3));
      out.solids.push(solid(lateral - 0.2, depth - 3.3, 1.1, 0.6));
      out.armchairs.push(facing(lateral + 1.6, depth - 3.3, lateral - 0.2, depth - 3.3));
      out.solids.push(solid(lateral + 1.6, depth - 3.3, 0.9, 0.9));
      const [rx, rz] = f.place(lateral + 0.4, depth - 3.5);
      out.rugs.push({
        x: rx,
        z: rz,
        w: f.alongX ? 4.4 : 3.4,
        d: f.alongX ? 3.4 : 4.4,
        color: "#a3563a",
      });
      const [px, pz] = f.place(lateral + 0.4, depth - 3.5);
      out.pendants.push({ x: px, z: pz });
      // Shelves along the side wall by the desks.
      out.shelves.push(facing(-(span - 0.5), 8, 0, 8));
      out.solids.push(solid(-(span - 0.5), 8, 0.7, 2.4));
    }
    // A whiteboard on the back wall beside the lead's desk.
    out.boards.push({ ...spot(span - 4.2, 0.55) });
    out.solids.push(solid(span - 4.2, 0.55, 0.9, 0.5));
  }

  // Plants flank the door; a mat inside it on the ground floor.
  for (const s of [-1, 1]) plant(s * 2.6, f.length - 0.9);
  if (options.ground) {
    const [mx, mz] = f.place(0, f.length - 1.1);
    out.rugs.push({
      x: mx,
      z: mz,
      w: f.alongX ? 2.6 : 1.5,
      d: f.alongX ? 1.5 : 2.6,
      color: "#2c2f36",
    });
  }

  // Ceiling lights on a grid.
  const nx = Math.max(1, Math.floor(f.width / 4.5));
  const nd = Math.max(1, Math.floor(f.length / 4.5));
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < nd; j++) {
      const [lx, lz] = f.place(((i + 0.5) / nx - 0.5) * f.width, ((j + 0.5) / nd) * f.length);
      out.lights.push({ x: lx, z: lz });
    }
  }
  return out;
}

type Face = "front" | "back" | "left" | "right";

/** Runs the theme's plan with placement helpers that keep corridors open and pieces apart. */
function themed(
  out: FurnitureLayout,
  theme: FloorTheme,
  f: Frame,
  h: {
    spot: (lat: number, dep: number, scale?: number) => Spot;
    solid: (lat: number, dep: number, w: number, d: number) => Rect;
    station: (lat: number, dep: number, scale?: number, dual?: boolean) => void;
    plant: (lat: number, dep: number, scale?: number) => void;
    facing: (lat: number, dep: number, toLat: number, toDep: number, scale?: number) => Spot;
  },
): void {
  const span = f.width / 2;
  const L = f.length;
  const occ: FrameRect[] = [];
  const rect = (lat: number, dep: number, a: number, b: number): FrameRect => ({
    a0: lat - a / 2,
    a1: lat + a / 2,
    b0: dep - b / 2,
    b1: dep + b / 2,
  });
  const hit = (r: FrameRect, q: FrameRect) =>
    r.a0 < q.a1 - 0.02 && r.a1 > q.a0 + 0.02 && r.b0 < q.b1 - 0.02 && r.b1 > q.b0 + 0.02;
  // Corridors that stay open: the way in, the elevator front and the stairs front.
  const reserved: FrameRect[] = [
    { a0: -1.7, a1: 1.7, b0: L - 3.6, b1: L },
    { a0: -span, a1: -span + 3.4, b0: 0, b1: 5.0 },
    { a0: span - 3.4, a1: span, b0: 0, b1: 5.6 },
  ];
  const inside = (r: FrameRect) =>
    r.a0 >= -span - 0.01 && r.a1 <= span + 0.01 && r.b0 >= -0.01 && r.b1 <= L + 0.01;
  const free = (lat: number, dep: number, a: number, b: number): boolean => {
    const r = rect(lat, dep, a, b);
    return inside(r) && ![...occ, ...reserved].some((q) => hit(r, q));
  };
  const faceYaw = (face: Face): number => {
    if (face === "front") return f.yaw;
    if (face === "back") return f.yaw + Math.PI;
    const [x0, z0] = f.place(0, 0);
    const [x1, z1] = f.place(face === "right" ? 1 : -1, 0);
    return Math.atan2(x1 - x0, z1 - z0);
  };
  const sideways = (face: Face) => face === "left" || face === "right";
  const ctx: Ctx = {
    f,
    span,
    L,
    out: out,
    free,
    reserve(lat, dep, a, b) {
      occ.push(rect(lat, dep, a, b));
    },
    put(kind, lat, dep, o = {}) {
      const w = o.w ?? 1;
      const d = o.d ?? 1;
      const face = o.face ?? "front";
      const [a, b] = sideways(face) ? [d, w] : [w, d];
      if (!free(lat, dep, a, b)) return false;
      occ.push(rect(lat, dep, a, b));
      const [x, z] = f.place(lat, dep);
      out.props.push({
        kind,
        x,
        z,
        yaw: faceYaw(face),
        w,
        d,
        h: o.h ?? 0,
        y: o.y ?? 0,
        ...(o.tone ? { tone: o.tone } : {}),
      });
      if (o.solid !== false) out.solids.push(h.solid(lat, dep, a, b));
      return true;
    },
    model(list, lat, dep, face, w, d, o = {}) {
      const [a, b] = sideways(face) ? [d, w] : [w, d];
      if (!free(lat, dep, a, b)) return false;
      occ.push(rect(lat, dep, a, b));
      const [x, z] = f.place(lat, dep);
      out[list].push({ x, z, yaw: faceYaw(face), scale: o.scale ?? 1 });
      if (o.solid !== false) out.solids.push(h.solid(lat, dep, a, b));
      return true;
    },
    deco(name, lat, dep, face, w, d, o = {}) {
      const [a, b] = sideways(face) ? [d, w] : [w, d];
      const [x, z] = f.place(lat, dep);
      if (!o.on) {
        if (!free(lat, dep, a, b)) return false;
        occ.push(rect(lat, dep, a, b));
        if (o.solid !== false) out.solids.push(h.solid(lat, dep, a, b));
      }
      out.deco.push({ name, x, z, yaw: faceYaw(face), scale: o.scale ?? 1, y: o.y ?? 0 });
      return true;
    },
    panel(kind, lat, dep, face, w, hgt, y, tone) {
      const [x, z] = f.place(lat, dep);
      out.panels.push({ kind, x, z, yaw: faceYaw(face), y, w, h: hgt, ...(tone ? { tone } : {}) });
    },
    rug(lat, dep, a, b, color) {
      const [x, z] = f.place(lat, dep);
      out.rugs.push({
        x,
        z,
        w: f.alongX ? a : b,
        d: f.alongX ? b : a,
        color,
      });
    },
    plant(lat, dep, scale = 1) {
      if (!free(lat, dep, 0.8 * scale, 0.8 * scale)) return false;
      occ.push(rect(lat, dep, 0.8 * scale, 0.8 * scale));
      h.plant(lat, dep, scale);
      return true;
    },
    pendant(lat, dep) {
      const [x, z] = f.place(lat, dep);
      out.pendants.push({ x, z });
    },
    chair(lat, dep) {
      out.chairs.push(h.spot(lat, dep));
      occ.push(rect(lat, dep, 0.8, 0.8));
    },
    station(lat, dep, o = {}) {
      const s = o.scale ?? 1;
      h.station(lat, dep, s, o.dual ?? false);
      occ.push(rect(lat, dep + 0.33 * s, 2.0 * s, 1.5 * s));
    },
    glassRoom(lat, dep, a, b, o = {}) {
      const sides = o.sides ?? ["front", "back", "left", "right"];
      const gap = o.gap ?? 1.1;
      const seg = (l0: number, l1: number, d0: number, d1: number) => {
        const [ax, az] = f.place(l0, d0);
        const [bx, bz] = f.place(l1, d1);
        const w = Math.max(0.06, Math.abs(ax - bx));
        const dd = Math.max(0.06, Math.abs(az - bz));
        out.glass.push({ x: (ax + bx) / 2, z: (az + bz) / 2, w, d: dd });
        occ.push({
          a0: Math.min(l0, l1) - 0.04,
          a1: Math.max(l0, l1) + 0.04,
          b0: Math.min(d0, d1) - 0.04,
          b1: Math.max(d0, d1) + 0.04,
        });
        // Dark rails and posts along the pane.
        const len = Math.hypot(l1 - l0, d1 - d0);
        const cl = (l0 + l1) / 2;
        const cd = (d0 + d1) / 2;
        const across = Math.abs(l1 - l0) >= Math.abs(d1 - d0);
        const [px, pz] = f.place(cl, cd);
        out.props.push({
          kind: "glassFrame",
          x: px,
          z: pz,
          yaw: across ? f.yaw : faceYaw("right"),
          w: len,
          d: 0.06,
          h: 0,
          y: 0,
        });
      };
      for (const side of sides) {
        const open = o.open === side;
        const run = (side === "front" || side === "back" ? a : b) / 2;
        const fixed =
          side === "front"
            ? dep + b / 2
            : side === "back"
              ? dep - b / 2
              : side === "left"
                ? lat - a / 2
                : lat + a / 2;
        const centre = side === "front" || side === "back" ? lat : dep;
        const parts: [number, number][] = open
          ? [
              [centre - run, centre - gap / 2],
              [centre + gap / 2, centre + run],
            ]
          : [[centre - run, centre + run]];
        for (const [c0, c1] of parts) {
          if (c1 - c0 < 0.1) continue;
          if (side === "front" || side === "back") seg(c0, c1, fixed, fixed);
          else seg(fixed, fixed, c0, c1);
        }
      }
    },
  };
  layoutTheme(theme, ctx);
}

/** Footprints of the models (meters, along the model's own x / z, before the quarter turn). */
const DESK = { w: 2.0, d: 0.95 };
const TABLE = { w: 1.35, d: 2.8 };
const PLANT_R = 0.3;

/**
 * Walking colliders for the furniture of a floor: desks, the meeting table, the leader's glass
 * walls (the door gap between the two front segments stays open), the plants and every other
 * solid piece (sofas, shelves, reception). All axis aligned (the models turn by quarter turns
 * only). `top` is the absolute height of the solid blocks, so they stop the walker instead of
 * being stepped onto. Chairs stay walkable-through (they roll).
 */
export function furnitureColliders(
  layout: FurnitureLayout,
  alongX: boolean,
  top: number,
): Collider[] {
  const out: Collider[] = [];
  // A model's x extent runs along the world X when the door wall runs along X, else along Z.
  const box = (x: number, z: number, w: number, d: number, scale = 1): Collider => {
    const sw = (alongX ? w : d) * scale;
    const sd = (alongX ? d : w) * scale;
    return { x, z, yaw: 0, hw: sw / 2, hd: sd / 2, top };
  };
  for (const d of layout.desks) out.push(box(d.x, d.z, DESK.w, DESK.d, d.scale));
  if (layout.table) {
    const t = layout.table;
    // The table is long from back to front, which is the other axis of the desk.
    out.push({
      x: t.x,
      z: t.z,
      yaw: 0,
      hw: (alongX ? TABLE.w : TABLE.d) / 2,
      hd: (alongX ? TABLE.d : TABLE.w) / 2,
      top,
    });
  }
  for (const g of layout.glass) out.push({ x: g.x, z: g.z, yaw: 0, hw: g.w / 2, hd: g.d / 2, top });
  for (const s of layout.solids)
    out.push({ x: s.x, z: s.z, yaw: 0, hw: s.w / 2, hd: s.d / 2, top });
  for (const p of layout.plants)
    out.push({ x: p.x, z: p.z, yaw: 0, hw: PLANT_R * p.scale, hd: PLANT_R * p.scale, top });
  return out;
}
